import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { CustomStateDto, WorkflowItemData, WorkflowStatus } from '@api/projects/interfaces';
import {ReportConfig} from '@interfaces/reports';


@Injectable({
	providedIn: 'root'
})
export class WorkflowRestService {

	private endpoint = environment.serverEndpoint;

	constructor(private httpClient: HttpClient) { }

	getWorkflowStatus$(projectID: string, participantID: string): Observable<WorkflowStatus> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${participantID}/status`;
		return this.httpClient.get<WorkflowStatus>(url);
	}

	getWorkflowStatus(projectID: string, participantID: string): Promise<WorkflowStatus> {
		return this.getWorkflowStatus$(projectID, participantID).toPromise();
	}

	getWorkflowItemStatus$(projectID: string, participantID: string, itemID: string): Observable<WorkflowItemData> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${participantID}/item/${itemID}/status`;
		return this.httpClient.get<WorkflowItemData>(url);
	}

	updateStatus$(projectID: string, pid: string, itemId: string, data: Partial<WorkflowItemData>): Observable<WorkflowItemData> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${pid}/item/${itemId}/status`;
		return this.httpClient.patch<WorkflowItemData>(url, data);
	}

	updateStatus(projectID: string, pid: string, itemId: string, data: Partial<WorkflowItemData>): Promise<WorkflowItemData> {
		return this.updateStatus$(projectID, pid, itemId, data).toPromise();
	}

	switchToState$(projectID: string, pid: string, itemId: string, newStateID: string): Observable<string> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${pid}/item/${itemId}/customstate/${newStateID}`;
		return this.httpClient.post<string>(url, null);
	}

	bulkSwitchToState$(projectID: string, newStateID: string, bulkOperations: CustomStateDto): Observable<string> {
		const url = `${this.endpoint}/api/projects/${projectID}/customstate/${newStateID}`;
		return this.httpClient.post<string>(url, bulkOperations);
	}

	deleteStatus$(projectID: string, pid: string, itemId: string): Observable<WorkflowItemData> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${pid}/item/${itemId}/status`;
		return this.httpClient.delete<WorkflowItemData>(url);
	}

	deleteStatus(projectID: string, pid: string, itemId: string): Promise<WorkflowItemData> {
		return this.deleteStatus$(projectID, pid, itemId).toPromise();
	}

	finishWorkflowItem(projectID: string, pid: string, itemId: string, data: WorkflowItemData): Promise<WorkflowItemData> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${pid}/item/${itemId}/data`;
		return this.httpClient.post<WorkflowItemData>(url, data).toPromise();
	}

	getWorkflowItemData$(projectID: string, participantID: string, WorkflowItemID: string): Observable<WorkflowItemData[]> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${participantID}/item/${WorkflowItemID}/data`;
		return this.httpClient.get<WorkflowItemData[]>(url);
	}

	deleteWorkflowItemData$(projectID: string, participantID: string, WorkflowItemID: string): Promise<WorkflowItemData> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${participantID}/item/${WorkflowItemID}/data`;
		return this.httpClient.delete<WorkflowItemData>(url).toPromise();
	}

	getWorkflowItemAggregate$(projectID: string, participantID: string, WorkflowItemID: string): Observable<WorkflowItemData> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${participantID}/item/${WorkflowItemID}/aggregate`;
		return this.httpClient.get<WorkflowItemData>(url);
	}

	getReportConfigs$(projectID: string): Observable<ReportConfig[]> {
		const url = `${this.endpoint}/api/projects/${projectID}/reports`;
		return this.httpClient.get<ReportConfig[]>(url);
	}

	getReportConfig$(projectID: string, reportID: string): Observable<ReportConfig> {
		const url = `${this.endpoint}/api/projects/${projectID}/reports/${reportID}`;
		return this.httpClient.get<ReportConfig>(url);
	}

	resetWorkflowItemData$(projectID: string, participantID: string, WorkflowItemID: string): Promise<WorkflowItemData> {
		const url = `${this.endpoint}/api/projects/${projectID}/participants/${participantID}/item/${WorkflowItemID}/data/runtriggers`;
		return this.httpClient.post<WorkflowItemData>(url, {}).toPromise();
	}
}
